import PortfolioModal from '@/components/common/modals/PortfolioModal';
import ResponsiveComponent from '@/components/common/ResponsiveComponent';
import ShareModal from '@/components/common/ShareModal';
import SkuPageDesktop from '@/components/sku/Desktop/SkuPageDesktop';
import SkuPageMobile from '@/components/sku/Mobile/SkuPageMobile';
import SkuLayout from '@/components/sku/SkuLayout';
import useSalesChart from '@/hooks/SalesChart/useSalesChart';
import useSkuPage from '@/hooks/useSkuPage';
import useWindowWidth from '@/hooks/useWindowWidth';
import { AskDTO, BidDTO, SkuDTO } from '@/types/api/sku';
import { SkuPageProps } from '@/types/props/pages';
import { useFetchBidsAsks, useFetchSkuId } from '@/useQuery/queries/sku';
import { LastViewedSku } from '@/utilites/last-viewed-skus';
import translationFallback from '@/utilites/translations/translationFallback';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { SkuViewTracker } from '@/components/sku/SkuViewTracker';
import useBackendUser from '@/hooks/useBackendUser';
import { useSkuKlaviyoPopup } from '@/components/Klaviyo/useSkuKlaviyoPopup';
const SkuPages = ({ sku, windowWidthSv, ownBidsAsks, spiritoryBottle }: SkuPageProps) => {
  const { data: user } = useBackendUser();
  const { t: tSku } = useTranslation('sku');
  const windowWidth = useWindowWidth(windowWidthSv);
  const bidsAsksQuery = useFetchBidsAsks(sku.slug);
  const skuIdQuery = useFetchSkuId(sku.slug);
  const router = useRouter();
  const [showShareModal, setShowShareModal] = useState(false);
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);

  const {
    lastActivities,
    skuCurrentMarketValue,
    skuTotalReturn,
    skuAverageSalePrice,
    orders,
    similarProductsQuery,
    ordersQuery,
    highestBid,
    memoizedFilteredAsk,
    skuTotalReturnPercentage,
  } = useSkuPage(
    bidsAsksQuery.data?.asks || [],
    bidsAsksQuery.data?.bids || [],
    sku.retailPrice,
    sku.slug,
    user?.id ?? null,
    false
  );

  const similarProductsSkus = similarProductsQuery.data || [];
  const {
    name: skuNameNullable,
    category,
    brand,
    bottleSizeInMl: skuBottleSizeInMl,
    volumeInPercent: skuVolumeInPercent,
    releaseDate: skuReleaseDate,
    distilledYear: skuDistilledYear,
    description: skuDescriptionNullable,
    mainImageUrl: skuImageUrl,
    retailPrice: skuRetailPrice,
  } = { ...sku };

  const skuCategoryName = category?.data?.attributes.name || '';
  const skuSlug = sku.slug;
  const skuName = skuNameNullable || '';
  const skuDescription = skuDescriptionNullable
    ? skuDescriptionNullable[(router.locale || 'en') as keyof typeof skuDescriptionNullable]
    : '';
  const skuMainImageUrl = skuImageUrl;
  const lastSale =
    orders.length > 0
      ? [...orders].sort(
          (a, b) =>
            new Date(b.skuOrderStatus.paid).getTime() - new Date(a.skuOrderStatus.paid).getTime()
        )[0].payValue
      : null;

  const {
    name: skuBrandName,
    region: skuBrandRegion,
    countryOfOrigin: skuBrandCountryOfOrigin,
  } = brand?.data?.attributes || {
    name: '',
    region: '',
    countryOfOrigin: '',
  };

  const handleClosePortfolioModal = () => setShowPortfolioModal(false);
  const handleOpenPortfolioModal = () => setShowPortfolioModal(true);

  useEffect(() => {
    if (skuIdQuery.data) {
      LastViewedSku({
        id: skuIdQuery.data,
      });
    }
  }, [skuIdQuery.data]);

  useSkuKlaviyoPopup();

  const { chartData, period, setPeriod } = useSalesChart(
    skuRetailPrice,
    orders,
    bidsAsksQuery.data?.bids || [],
    bidsAsksQuery.data?.asks || []
  );

  return (
    <SkuLayout
      skuName={skuName}
      country={skuBrandCountryOfOrigin}
      category={skuCategoryName}
      description_language={[
        translationFallback('meta_desc_1', tSku('meta_desc_1'), 'Buy and sell'),
        translationFallback('meta_desc_2', tSku('meta_desc_2'), 'from'),
        translationFallback('meta_desc_3', tSku('meta_desc_3'), 'and thousands of other verified'),
        translationFallback(
          'meta_desc_4',
          tSku('meta_desc_4'),
          'on Spiritory, the leading collectabile marketplace.'
        ),
      ]}
      windowTitle={`${skuName} | ${skuCategoryName} | Spiritory`}
    >
      {skuIdQuery.data && <SkuViewTracker skuId={skuIdQuery.data} />}
      <ResponsiveComponent
        windowWidth={windowWidth}
        DesktopComponent={
          <SkuPageDesktop
            skuMainImageUrl={skuMainImageUrl}
            skuSlug={sku.slug}
            skuName={skuName}
            userAuthenticated={Boolean(user?.id)}
            tSkus={tSku}
            skuCategoryName={skuCategoryName}
            skuCountryOfOrigin={skuBrandCountryOfOrigin}
            handleOpenPortfolioModal={handleOpenPortfolioModal}
            skuBrandName={skuBrandName}
            skuBottleSizeInMl={skuBottleSizeInMl}
            skuReleaseDate={skuReleaseDate}
            skuVolumeInPercent={skuVolumeInPercent}
            skuBrandCountryOfOrigin={skuBrandCountryOfOrigin}
            skuBrandRegion={skuBrandRegion}
            skuDistilledYear={skuDistilledYear}
            skuDescription={skuDescription}
            skuRetailPrice={skuRetailPrice}
            skuAsks={bidsAsksQuery.data?.asks || []}
            skuBids={bidsAsksQuery.data?.bids || []}
            skuOrders={orders}
            skuTotalReturn={skuTotalReturn}
            skuAverageSalePrice={skuAverageSalePrice}
            skuCurrentMarketValue={skuCurrentMarketValue}
            similarSkus={similarProductsSkus}
            lastActivities={lastActivities}
            ownBidsAsks={ownBidsAsks}
            memoizedFilteredAsk={memoizedFilteredAsk}
            highestBid={highestBid}
            lastSale={lastSale}
            bidsAskLoading={bidsAsksQuery.isLoading}
            loadingMarketData={ordersQuery.isLoading || bidsAsksQuery.isLoading}
            chartData={chartData}
            period={period}
            setPeriod={setPeriod}
            spiritoryBottle={false}
            isAdmin={user?.isAdmin || false}
            lastActivitiesLoading={ordersQuery.isLoading || bidsAsksQuery.isLoading}
            skuTotalReturnPercentage={skuTotalReturnPercentage}
            userId={user?.id || null}
            setShowShareModal={setShowShareModal}
            sku={
              {
                ...sku,
                orders: orders,
                retailPrice: skuRetailPrice,
                name: skuName,
                slug: skuSlug,
                bottleSizeInMl: skuBottleSizeInMl,
                volumeInPercent: skuVolumeInPercent,
                lowestAsk: memoizedFilteredAsk,
                asks: (bidsAsksQuery.data?.asks || []) as AskDTO[],
                bids: (bidsAsksQuery.data?.bids || []) as BidDTO[],
                id: skuIdQuery.data || '',
                userRequestingId: user?.id || null,
                spiritoryBottle: spiritoryBottle || false,
              } as unknown as SkuDTO
            }
          />
        }
        MobileComponent={
          <SkuPageMobile
            skuCategoryName={skuCategoryName}
            skuCountryOfOrigin={skuBrandCountryOfOrigin}
            skuName={skuName}
            skuSlug={skuSlug}
            skuMainImageUrl={skuMainImageUrl}
            setShowShareModal={setShowShareModal}
            setShowPortfolioModal={setShowPortfolioModal}
            skuBrandName={skuBrandName}
            skuReleaseDate={skuReleaseDate}
            skuBottleSizeInMl={skuBottleSizeInMl}
            skuVolumeInPercent={skuVolumeInPercent}
            memoizedFilteredAsk={memoizedFilteredAsk}
            spiritoryBottle={false}
            isAdmin={user?.isAdmin || false}
            userId={user?.id || null}
            bidsAskLoading={bidsAsksQuery.isLoading}
            highestBid={highestBid}
            skuAsks={bidsAsksQuery.data?.asks || []}
            skuBids={bidsAsksQuery.data?.bids || []}
            skuOrders={orders}
            skuRetailPrice={skuRetailPrice}
            loadingMarketData={ordersQuery.isLoading || bidsAsksQuery.isLoading}
            lastSale={lastSale}
            skuDescription={skuDescription}
            skuBrandCountryOfOrigin={skuBrandCountryOfOrigin}
            skuBrandRegion={skuBrandRegion}
            skuDistilledYear={skuDistilledYear}
            lastActivitiesLoading={ordersQuery.isLoading || bidsAsksQuery.isLoading}
            chartData={chartData}
            period={period}
            setPeriod={setPeriod}
            similarProductsSkus={similarProductsSkus}
            lastActivities={lastActivities}
            skuAverageSalePrice={skuAverageSalePrice}
            skuCurrentMarketValue={skuCurrentMarketValue}
            skuTotalReturn={skuTotalReturn}
            skuTotalReturnPercentage={skuTotalReturnPercentage}
            ownBidsAsks={ownBidsAsks}
            sku={sku as unknown as SkuDTO}
          />
        }
      />
      <PortfolioModal
        showPortfolioModal={showPortfolioModal}
        handleClosePortfolioModal={handleClosePortfolioModal}
        skuSlug={skuSlug}
        skuMainImageUrl={skuMainImageUrl}
        skuRetailPrice={skuRetailPrice}
        skuName={skuName}
        skuBottleSizeInMl={skuBottleSizeInMl}
        skuVolumeInPercent={skuVolumeInPercent}
        skuReleaseDate={skuReleaseDate}
      />
      <ShareModal
        showShareModal={showShareModal}
        handleCloseShareModal={() => setShowShareModal(false)}
        url={`${process.env.NEXT_PUBLIC_BASE_URL}/${skuSlug}`}
        title={skuName ?? ''}
      />
    </SkuLayout>
  );
};

export default SkuPages;
